import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "../api";
import { formatApiError } from "../utils/formatApiError";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { SKILL_LEVELS } from "../constants/skillLevels";
import { validatePhone } from "../utils/validatePhone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAHJONG_STYLES = [
  "Taiwanese", "Willingdon", "ATF Western", "Singapore",
  "International", "Japanese", "Hong Kong", "American", "Chinese",
];

// Two-stage unified login. Replaces the old LoginPage + RegisterPage split.
//   Stage A: Email entry. POST /auth/start. Backend decides one of three things.
//   Stage B: Profile completion (revealed when /auth/start returns
//            needs_completion or needs_registration). On submit, POST
//            /auth/complete-and-send. Either way the magic link gets sent.
//   Stage C: "Check your email" success view.
//
// Accepts URL params ?event=<id>&signup=<play|class> and forwards them as
// intent_event_id / intent_signup_type so the magic link auto-creates a
// Player on verification.

const LoginPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const intentEventId = searchParams.get("event") || null;
  const intentSignupType =
    searchParams.get("signup") === "class" ? "class"
    : searchParams.get("signup") === "play" ? "play"
    : null;

  // The old LoginPage supported a redirectPath via location.state.from for
  // pages that bounced unauthenticated users to /login. Keep that, but the
  // intent-event redirect (`/events/{id}`) is computed server-side and takes
  // precedence when ?event= is present.
  const redirectPath = typeof location.state?.from === "string"
    ? location.state.from
    : (location.state?.from?.pathname || null);

  const [stage, setStage] = useState("email");          // "email" | "form" | "sent"
  const [email, setEmail] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    style_skills: [{ style: "", skill_level: "" }],
  });
  const [profileMode, setProfileMode] = useState("new"); // "new" | "complete"
  // Set of field-group names ("first_name" | "last_name" | "phone" |
  // "style_skills") whose inputs we render in the form. In "new" mode
  // every group is rendered. In "complete" mode only the genuinely
  // missing groups appear — the user is never asked to re-enter
  // information they've already provided.
  const [missingFields, setMissingFields] = useState(new Set([
    "first_name", "last_name", "phone", "style_skills",
  ]));

  // Resend state for the success view
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState("");

  useEffect(() => {
    const msg = sessionStorage.getItem("auth_message");
    if (msg) {
      setAuthMessage(msg);
      sessionStorage.removeItem("auth_message");
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) { clearInterval(t); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // ------------------------------------------------------------------
  // Stage A: email submit
  // ------------------------------------------------------------------
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const trimmed = email.trim();
    try {
      const body = {
        email: trimmed,
        intent_event_id: intentEventId,
        intent_signup_type: intentSignupType,
        origin: window.location.origin,
      };
      if (redirectPath) body.redirect = redirectPath;
      const res = await api.post(`${API}/auth/start`, body, { timeout: 30000 });
      const status = res.data?.status;
      if (status === "magic_link_sent") {
        setStage("sent");
        setCooldown(60);
        toast.success("Check your email for the login link!");
      } else if (status === "needs_completion") {
        const pf = res.data?.prefill || {};
        // Compute the set of genuinely missing fields. The backend only
        // returns a `needs_completion` status when at least one field is
        // missing, but we still classify each independently so the form
        // shows exactly the inputs the user needs to fill — and nothing
        // they've already provided.
        const missing = new Set();
        if (!pf.first_name || !String(pf.first_name).trim()) missing.add("first_name");
        if (!pf.last_name || !String(pf.last_name).trim()) missing.add("last_name");
        if (!pf.phone || !String(pf.phone).trim()) missing.add("phone");
        if (!Array.isArray(pf.style_skills) || pf.style_skills.length === 0) {
          missing.add("style_skills");
        }
        setMissingFields(missing);
        setProfile({
          first_name: pf.first_name || "",
          last_name: pf.last_name || "",
          phone: pf.phone || "",
          style_skills: missing.has("style_skills")
            ? [{ style: "", skill_level: "" }]
            : pf.style_skills,
        });
        setProfileMode("complete");
        setStage("form");
      } else {
        // needs_registration — empty profile form, all fields rendered
        setProfile({
          first_name: "",
          last_name: "",
          phone: "",
          style_skills: [{ style: "", skill_level: "" }],
        });
        setMissingFields(new Set(["first_name", "last_name", "phone", "style_skills"]));
        setProfileMode("new");
        setStage("form");
      }
    } catch (err) {
      if (!err.response) {
        setError("This is taking longer than expected. Please try again in a moment.");
      } else if (err.response.status === 429) {
        setError("Too many attempts — please wait a minute and try again.");
      } else {
        setError(formatApiError(err, "Something went wrong. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Stage B: profile completion submit
  // ------------------------------------------------------------------
  const handleStyleSkillAdd = () => {
    setProfile(p => ({ ...p, style_skills: [...p.style_skills, { style: "", skill_level: "" }] }));
  };
  const handleStyleSkillChange = (idx, field, value) => {
    setProfile(p => {
      const next = [...p.style_skills];
      next[idx] = { ...next[idx], [field]: value };
      return { ...p, style_skills: next };
    });
  };
  const handleStyleSkillRemove = (idx) => {
    setProfile(p => ({ ...p, style_skills: p.style_skills.filter((_, i) => i !== idx) }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPhoneError("");

    // Only validate fields the user actually had to fill in. Pre-existing
    // prefill values are already authoritative — if the form didn't render
    // an input for them, the user couldn't have changed them.
    if (missingFields.has("first_name") && !profile.first_name.trim()) {
      return setError("First name is required.");
    }
    if (missingFields.has("last_name") && !profile.last_name.trim()) {
      return setError("Last name is required.");
    }
    if (missingFields.has("phone")) {
      if (!profile.phone || !profile.phone.trim()) {
        return setPhoneError("Please enter your phone number");
      }
      const phoneErr = validatePhone(profile.phone);
      if (phoneErr) return setPhoneError(phoneErr);
    }
    if (missingFields.has("style_skills")) {
      if (profile.style_skills.length === 0) {
        return setError("Please add at least one style you play.");
      }
      if (profile.style_skills.some(s => !s.style || !s.skill_level)) {
        return setError("Please complete all style and skill level fields.");
      }
    }

    setSubmitting(true);
    try {
      const body = {
        email: email.trim(),
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim(),
        phone: profile.phone.trim(),
        style_skills: profile.style_skills,
        intent_event_id: intentEventId,
        intent_signup_type: intentSignupType,
        origin: window.location.origin,
      };
      if (redirectPath) body.redirect = redirectPath;
      await api.post(`${API}/auth/complete-and-send`, body, { timeout: 30000 });
      setStage("sent");
      setCooldown(60);
      toast.success("Check your email for the login link!");
    } catch (err) {
      const errorCode = err.response?.headers?.["x-error-code"];
      if (errorCode === "phone_required") {
        setPhoneError("Please enter your phone number");
      } else if (!err.response) {
        setError("This is taking longer than expected. Please try again in a moment.");
      } else if (err.response.status === 429) {
        setError("Too many attempts — please wait a minute and try again.");
      } else {
        setError(formatApiError(err, "Something went wrong. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Stage C: resend
  // ------------------------------------------------------------------
  const handleResend = async () => {
    setResending(true);
    setResendStatus("");
    try {
      // After completion the user record is already created/updated, so just
      // restart from /auth/start — it'll send a fresh magic link.
      const body = {
        email: email.trim(),
        intent_event_id: intentEventId,
        intent_signup_type: intentSignupType,
        origin: window.location.origin,
      };
      if (redirectPath) body.redirect = redirectPath;
      await api.post(`${API}/auth/start`, body, { timeout: 30000 });
      setCooldown(60);
      setResendStatus("sent");
      setTimeout(() => setResendStatus(""), 3000);
    } catch (err) {
      setResendStatus(err.response ? "error" : "slow");
      setTimeout(() => setResendStatus(""), 3000);
    } finally {
      setResending(false);
    }
  };

  // ==================================================================
  // RENDER
  // ==================================================================
  if (stage === "sent") {
    return (
      <div>
        <Header pageTitle="Check Your Email" />
        <div className="container" style={{ maxWidth: "768px" }}>
          <div style={{ maxWidth: "500px", margin: "0 auto" }}>
            <div className="card" style={{ textAlign: "center", padding: "3rem" }}>
              <Mail size={64} color="#1B4D3E" style={{ margin: "0 auto 1.5rem" }} />
              <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#1B4D3E" }}>
                Login Link Sent!
              </h2>
              <p style={{ color: "#666660", marginBottom: "1.5rem", lineHeight: "1.6" }}>
                We've sent a login link to <strong>{email}</strong>. Click the link in the email to log in.
              </p>
              <p style={{ color: "#666660", fontSize: "0.875rem" }}>
                Check your spam folder if you don't see it.
              </p>
              <div style={{ marginTop: "1.5rem" }}>
                <p style={{ color: "#666660", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                  Still nothing?
                </p>
                <button
                  onClick={handleResend}
                  className="btn btn-primary"
                  style={{ minWidth: "160px" }}
                  disabled={cooldown > 0 || resending}
                >
                  {resending
                    ? "Sending..."
                    : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
                </button>
                {resendStatus === "sent" && (
                  <p style={{ color: "#2E7D32", fontSize: "0.85rem", marginTop: "0.5rem" }}>Sent!</p>
                )}
                {resendStatus === "slow" && (
                  <p style={{ color: "#856404", fontSize: "0.85rem", marginTop: "0.5rem" }}>Still sending — check your inbox in a moment.</p>
                )}
                {resendStatus === "error" && (
                  <p style={{ color: "#C62828", fontSize: "0.85rem", marginTop: "0.5rem" }}>Failed to send. Try again.</p>
                )}
              </div>
              <button
                onClick={() => { setStage("email"); setCooldown(0); setResendStatus(""); }}
                style={{
                  marginTop: "1rem",
                  background: "none",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "0.5rem 1.25rem",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                }}
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (stage === "form") {
    // Defensive: if profileMode is "complete" but the prefill turned out
    // to actually have all four fields, missingFields is empty. We'd
    // render a form with no inputs — pointless. The backend's
    // `needs_completion` status guarantees ≥1 missing field, but we
    // guard anyway.
    if (profileMode === "complete" && missingFields.size === 0) {
      // Auto-submit the prefill rather than show an empty form. This
      // can't happen given the current backend logic; the branch exists
      // so a future relaxation of `is_profile_complete` doesn't surprise us.
      return null;
    }
    const headingTitle = profileMode === "complete" ? "We just need a few more details" : "Tell us about you";
    const headingSub = profileMode === "complete"
      ? "Fill these in and we'll send your login link."
      : "Quick — we just need a few details, then we'll send your login link.";
    const showFirstLastNames = missingFields.has("first_name") || missingFields.has("last_name");
    return (
      <div>
        <SEO title="Sign In" description="" path="/login" noindex />
        <Header pageTitle="Sign In" />
        <div className="container">
          <div style={{ maxWidth: "500px", margin: "0 auto" }}>
            <div className="card">
              <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                <Mail size={48} color="#1B4D3E" style={{ margin: "0 auto 1rem" }} />
                <h2 style={{ fontSize: "1.25rem", color: "#1B4D3E", marginBottom: "0.5rem" }}>
                  {headingTitle}
                </h2>
                <p style={{ fontSize: "0.875rem", color: "#666660" }}>
                  {headingSub}
                </p>
              </div>
              <form onSubmit={handleProfileSubmit}>
                <div style={{ marginBottom: "1rem", fontSize: "0.875rem", color: "#666" }}>
                  Email: <strong>{email}</strong>{" "}
                  <button
                    type="button"
                    onClick={() => { setStage("email"); setError(""); setPhoneError(""); }}
                    style={{ background: "none", border: "none", color: "#1B4D3E", textDecoration: "underline", cursor: "pointer", fontSize: "0.875rem", padding: 0 }}
                  >
                    change
                  </button>
                </div>

                {showFirstLastNames && (
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                    {missingFields.has("first_name") && (
                      <div style={{ flex: 1 }}>
                        <label className="label">First name</label>
                        <input
                          type="text"
                          className="input"
                          value={profile.first_name}
                          onChange={(e) => setProfile(p => ({ ...p, first_name: e.target.value }))}
                          required
                        />
                      </div>
                    )}
                    {missingFields.has("last_name") && (
                      <div style={{ flex: 1 }}>
                        <label className="label">Last name</label>
                        <input
                          type="text"
                          className="input"
                          value={profile.last_name}
                          onChange={(e) => setProfile(p => ({ ...p, last_name: e.target.value }))}
                          required
                        />
                      </div>
                    )}
                  </div>
                )}

                {missingFields.has("phone") && (
                  <div style={{ marginBottom: "1.25rem" }}>
                    <label className="label" htmlFor="phone">
                      Phone number <span style={{ color: "#D32F2F" }}>*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      className="input"
                      value={profile.phone}
                      onChange={(e) => {
                        setProfile(p => ({ ...p, phone: e.target.value }));
                        if (phoneError) setPhoneError("");
                      }}
                      required
                      placeholder="+91 98XXX XXXXX"
                      style={phoneError ? { borderColor: "#d32f2f" } : undefined}
                    />
                    {phoneError && (
                      <p style={{ color: "#d32f2f", fontSize: "0.8rem", marginTop: "0.25rem" }}>{phoneError}</p>
                    )}
                    <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.35rem" }}>
                      Used only for event-related updates.
                    </p>
                  </div>
                )}

                {missingFields.has("style_skills") && (
                  <div style={{ marginBottom: "1.25rem" }}>
                    <p style={{ fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "#1B4D3E", marginBottom: "0.5rem" }}>
                      Your Mahjong
                    </p>
                    {profile.style_skills.map((entry, idx) => (
                      <div key={idx} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "center" }}>
                        <select
                          value={entry.style}
                          onChange={e => handleStyleSkillChange(idx, "style", e.target.value)}
                          style={{ flex: 1, padding: "0.5rem 0.4rem", border: "1px solid #DDD", borderRadius: "6px", fontSize: "0.85rem" }}
                        >
                          <option value="">Style...</option>
                          {MAHJONG_STYLES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select
                          value={entry.skill_level}
                          onChange={e => handleStyleSkillChange(idx, "skill_level", e.target.value)}
                          style={{ flex: 1, padding: "0.5rem 0.4rem", border: "1px solid #DDD", borderRadius: "6px", fontSize: "0.85rem" }}
                        >
                          <option value="">Level...</option>
                          {SKILL_LEVELS.map(sl => <option key={sl.value} value={sl.value}>{sl.value}</option>)}
                        </select>
                        {profile.style_skills.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleStyleSkillRemove(idx)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "1.2rem", padding: "0 0.25rem", lineHeight: 1, flexShrink: 0 }}
                          >×</button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleStyleSkillAdd}
                      style={{ background: "none", border: "1px dashed #1B4D3E", color: "#1B4D3E", borderRadius: "6px", padding: "0.4rem 0.75rem", fontSize: "0.82rem", cursor: "pointer", width: "100%" }}
                    >
                      + Add another style
                    </button>
                  </div>
                )}

                {error && (
                  <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", color: "#C62828", fontSize: "0.9rem" }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "100%" }}
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Send login link"}
                </button>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Stage A: email entry
  return (
    <div>
      <SEO
        title="Sign In"
        description="Sign in to PlayMahjong.in to manage your event signups, view your upcoming games, and connect with other players."
        path="/login"
        noindex
      />
      <Header pageTitle="Sign In" />
      <div className="container">
        <div style={{ maxWidth: "400px", margin: "0 auto" }}>
          {authMessage && (
            <div style={{ background: "#FFF3CD", border: "1px solid #FFEAA7", borderRadius: "8px", padding: "0.875rem 1rem", marginBottom: "1rem", color: "#856404", fontSize: "0.9rem" }}>
              {authMessage}
            </div>
          )}
          <div className="card">
            <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              <Mail size={48} color="#1B4D3E" style={{ margin: "0 auto 1rem" }} />
              <h2 style={{ fontSize: "1.25rem", color: "#1B4D3E", marginBottom: "0.5rem" }}>
                Sign in or create your account
              </h2>
              <p style={{ fontSize: "0.875rem", color: "#666660" }}>
                Enter your email — we'll continue from there.
              </p>
            </div>

            <form onSubmit={handleEmailSubmit}>
              <div style={{ marginBottom: "1.5rem" }}>
                <label className="label" htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  data-testid="email-input"
                  style={{ fontSize: "1rem" }}
                />
                <p style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.5rem" }}>
                  We keep emails minimal — just confirmations and essential updates.
                </p>
              </div>

              {error && (
                <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", color: "#C62828", fontSize: "0.9rem" }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: "100%" }}
                disabled={submitting}
                data-testid="send-magic-link"
              >
                {submitting ? "Continuing..." : "Continue"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;
